/**
 * @fileoverview Inline chat chart card. Renders a ChartSpec and (when a save
 * handler is supplied) offers "Save to canvas".
 */
import { useState } from "react";
import { BookmarkPlus, Check } from "lucide-react";
import type { ChartSpec } from "@cvx/lib/charts/spec";
import { ChartRenderer } from "../chartRenderer";

export function ChartCard({
  spec,
  onSaveToCanvas,
}: {
  spec: ChartSpec;
  onSaveToCanvas?: (spec: ChartSpec) => Promise<void> | void;
}) {
  const [saved, setSaved] = useState(false);
  return (
    <div className="not-prose my-3 rounded-lg border border-border bg-background p-3 shadow-sm">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{spec.title}</p>
          {spec.description && (
            <p className="text-xs text-muted-foreground">{spec.description}</p>
          )}
        </div>
        {onSaveToCanvas && (
          <button
            type="button"
            disabled={saved}
            onClick={async () => {
              try {
                await onSaveToCanvas(spec);
                setSaved(true);
              } catch {
                // The save handler surfaces its own error toast.
              }
            }}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20 disabled:opacity-60"
          >
            {saved ? (
              <Check className="h-3 w-3" />
            ) : (
              <BookmarkPlus className="h-3 w-3" />
            )}
            {saved ? "Saved" : "Save to canvas"}
          </button>
        )}
      </div>
      <ChartRenderer spec={spec} />
    </div>
  );
}
